'use client';

import { type Component, type ComponentProps } from '../../types/component';
import { CoreIcon } from '../core-icon/CoreIcon';
import {
  ChipBase,
  type ChipBaseWithAvatar,
  type ChipBaseWithIcon,
} from './internal/ChipBase';

interface RemovableChipSharedProps {
  /**
   * Accessible name for the remove action (e.g. `'Remove WiFi filter'`).
   */
  'aria-label': string;
}

interface RemovableChipWithIcon
  extends Omit<ChipBaseWithIcon, 'isSelected'>,
    RemovableChipSharedProps {}

interface RemovableChipWithAvatar
  extends Omit<ChipBaseWithAvatar, 'isSelected'>,
    RemovableChipSharedProps {}

type RemovableChipBaseProps = RemovableChipWithIcon | RemovableChipWithAvatar;

type RemovableChipProps = ComponentProps<
  RemovableChipBaseProps,
  'button',
  'disabled'
>;

type RemovableChipComponent = Component<
  RemovableChipBaseProps,
  'button',
  'disabled'
>;

const RemovableChip: RemovableChipComponent = (props) => {
  const {
    children,
    icon,
    avatar,
    size,
    isDisabled,
    isFullWidth,
    elevation,
    ...rest
  } = props;

  const leadingProps =
    icon != null ? { icon } : avatar != null ? { avatar } : {};

  return (
    <ChipBase
      {...rest}
      {...leadingProps}
      size={size}
      isDisabled={isDisabled}
      isFullWidth={isFullWidth}
      elevation={elevation}
      trailing={<CoreIcon name="cross" size="xs" />}
      isSelected
    >
      {children}
    </ChipBase>
  );
};

RemovableChip.displayName = 'RemovableChip';

export { RemovableChip, type RemovableChipProps, type RemovableChipBaseProps };
